import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { API_URL } from '../../config';
import { useTheme } from '@mui/material';
import { tokens } from '../../utils/theme';
import { Panel, PanelHeader, PanelTitle, TabsNav, Tab, Btn, BtnIcon } from '../../components/SharedPageStyles';
import TradingProfile from '../trading-profile/TradingProfile';
import Settings from '../settings/Settings';
import AccountCard from './AccountCard';

import { useJournalData } from '../../context/JournalContext';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? '#1e2130' : '#ffffff'};
  padding: 24px;
  border-radius: 8px;
  width: 400px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px 0;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
`;

const ModalText = styled.p`
  color: ${props => tokens(props.theme.palette.mode).grey[300]};
  margin-bottom: 24px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    padding: 20px;
    overflow-y: auto;
`;




const ProfileSettings = () => {
    const theme = useTheme();

    const [activeTab, setActiveTab] = useState('settings'); // Default to settings (General)
    const [viewMode, setViewMode] = useState('accounts'); // Default to accounts list
    const [isEditMode, setIsEditMode] = useState(false);
    const { userProfile, updateUserProfile, accounts, fetchAccounts, switchAccount } = useJournalData();
    // const [accounts, setAccounts] = useState(MOCK_ACCOUNTS); // Removed local state, using context

    const [pendingDeletions, setPendingDeletions] = useState(new Set());


    // Sync current active account with userProfile context
    const activeAccountId = userProfile?.id || 1;

    useEffect(() => {
        // Ensure the current userProfile reflects one of our mock accounts if possible
        const currentAccount = accounts.find(a => a.id === activeAccountId) || accounts[0];

        if (currentAccount && (!userProfile.name || !userProfile.id)) {
            updateUserProfile({
                ...userProfile,
                id: currentAccount.id,
                name: currentAccount.name,
                initialBalance: currentAccount.initialBalance,
                firm: currentAccount.firm,
                platform: currentAccount.platform,
                currency: currentAccount.currency === 'USD' ? 'USD ($)' : 'EUR (€)',
                status: "Active"
            });
        }
    }, [activeAccountId, accounts, userProfile, updateUserProfile]);

    const activeTabRef = React.useRef(null);

    // State for creating new account
    const [isCreating, setIsCreating] = useState(false);
    const [draftAccount, setDraftAccount] = useState(null);

    // State for viewing account details (can be different from active account)
    const [viewedAccount, setViewedAccount] = useState(null);

    // Reset viewed account when view changes to accounts list
    const handleSaveAccounts = () => {
        if (pendingDeletions.size === 0) {
            setIsEditMode(false);
            return;
        }
        executeSaveAccounts(); // Bypass the final confirmation dialog
    };

    const executeSaveAccounts = async () => {
        try {
            // Execute deletions
            for (const id of pendingDeletions) {
                await axios.delete(`${API_URL}/accounts/${id}`);
            }

            // Refresh accounts
            const updatedAccounts = await fetchAccounts();

            // Check active account
            if (pendingDeletions.has(userProfile.id)) {
                // Active account was deleted
                const fallback = updatedAccounts.find(a => !pendingDeletions.has(a.id));
                if (fallback) {
                    handleSwitchAccount(fallback);
                } else {
                    // No accounts left or fallback failed
                    window.location.reload();
                }
            }

            setPendingDeletions(new Set());
            setIsEditMode(false);
        } catch (error) {
            console.error("Error deleting accounts:", error);
            alert("Failed to delete some accounts.");
        }
    };

    const handleCancelAccounts = () => {
        setPendingDeletions(new Set());
        setIsEditMode(false);
    };

    const handleSwitchAccount = (account) => {
        // Update global profile context and fetch trades
        switchAccount(account);

        // If we are currently viewing details, switch the view to the new active account
        if (viewMode === 'details') {
            setViewedAccount(account);
        }
        // Do not close edit mode on switch, user might want to continue editing
    };

    // Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);

    const handleDeleteAccount = (account) => {
        setAccountToDelete(account);
        setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (accountToDelete) {
            const newDeletions = new Set(pendingDeletions);
            newDeletions.add(accountToDelete.id);
            setPendingDeletions(newDeletions);
            setDeleteModalOpen(false);
            setAccountToDelete(null);
        }
    };

    const cancelDelete = () => {
        setDeleteModalOpen(false);
        setAccountToDelete(null);
    };

    const handleManage = (account) => {
        setViewedAccount(account);
        setViewMode('details');
        setActiveTab('settings');
    };

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
    };

    const handleAddAccount = () => {
        const newDraft = {
            id: null,
            name: "",
            creation_date: new Date().toLocaleDateString(),
            account_type: "Firm",
            currency: "USD",
            initialBalance: 100000,
            broker: "",
            rules: {}
        };
        setDraftAccount(newDraft);
        setIsCreating(true);
        setViewedAccount(null);
        setViewMode('details');
        setActiveTab('settings');
        setIsEditMode(true);
    };

    const handleChildSave = async (data) => {
        try {
            if (isCreating) {
                // Creation Logic
                const newAccountPayload = {
                    ...draftAccount,
                    ...data,
                    name: data.name || draftAccount.name || `${data.broker}`
                };

                const response = await axios.post(`${API_URL}/accounts`, newAccountPayload);
                if (response.data && response.data.id) {
                    // Refresh accounts list
                    const updatedAccounts = await fetchAccounts();

                    // Switch to new account
                    const newAccount = updatedAccounts.find(a => a.id === response.data.id);
                    if (newAccount) {
                        handleSwitchAccount(newAccount);
                    }

                    setIsCreating(false);
                    setDraftAccount(null);
                    setIsEditMode(false);
                }
            } else {
                // Edit Logic
                const targetAccountId = viewedAccount?.id || userProfile.id;

                if (activeTab === 'settings') {
                    // Update General Settings
                    console.log("Saving Account Settings:", { targetAccountId, data });
                    try {
                        const response = await axios.put(`${API_URL}/accounts/${targetAccountId}`, data);
                        console.log("Save Response:", response);
                    } catch (err) {
                        console.error("Save Error Details:", err.response || err);
                        throw err;
                    }

                    // Refresh accounts
                    const updatedAccounts = await fetchAccounts();

                    // If editing active account, update global context
                    if (targetAccountId === userProfile.id) {
                        const updatedProfile = updatedAccounts.find(a => a.id === targetAccountId);
                        if (updatedProfile) {
                            updateUserProfile({
                                ...updatedProfile,
                                currency: updatedProfile.currency === 'USD' ? 'USD ($)' : 'EUR (€)',
                                status: "Active"
                            });
                        }
                    }

                    // Update viewed account
                    if (viewedAccount && viewedAccount.id === targetAccountId) {
                        const updatedViewed = updatedAccounts.find(a => a.id === targetAccountId);
                        if (updatedViewed) setViewedAccount(updatedViewed);
                    }

                } else if (activeTab === 'profile') {
                    // Update Rules
                    // The backend expects POST /accounts/{id}/rules with the rule object
                    // But here 'data' might be the whole rules object or a specific rule.
                    // Based on previous context, 'data' seems to be the full rules object.
                    // We need to iterate or send bulk? 
                    // Let's assume the backend handles individual rule updates or we iterate here.
                    // Actually, the backend `update_account_rules` (POST) takes a list of rules or single?
                    // Let's check backend... it takes `AccountRule` model one by one? 
                    // No, wait. The user earlier added `POST /accounts/{id}/rules` which calls `update_account_rules`.
                    // Let's assume we send the rules payload.

                    // Code review: `update_account_rules` in service.py iterates and updates.
                    // But wait, the frontend sends a `rules` object like { "ruleName": "value", ... }.
                    // We need to adapt this to what the backend expects.

                    // Backend expects: List[AccountRule]? Or just specific fields?
                    // Let's look at `app.py`. `update_account_rules_endpoint` takes `rules: List[AccountRule]`.
                    // So we must transform the `data` object into a list.

                    const rulesList = Object.entries(data).map(([key, value]) => ({
                        account_id: targetAccountId,
                        rule_name: key,
                        rule_value: String(value), // Ensure string for DB
                        is_active: true
                    }));

                    await axios.post(`${API_URL}/accounts/${targetAccountId}/rules`, rulesList);

                    // Refresh not strictly needed for rules unless we reload them, 
                    // but good practice to sync.
                    // await fetchAccounts(); // Rules are usually loaded on demand or attached?
                    // For now, update local state or just finish.
                }
                setIsEditMode(false);
            }
        } catch (error) {
            console.error("Error saving account settings:", error);
            // Optionally show user feedback
        }
    };

    // Helper to get current profile data for children
    const getCurrentProfileData = () => {
        if (isCreating) return draftAccount;

        // Determine the base data
        const baseData = viewedAccount || userProfile;

        // Return with calculated status
        const isActive = (baseData.id === userProfile.id);

        return {
            ...baseData,
            status: isActive ? "Active" : "Inactive"
        };
    };

    const handleDetailsSave = () => {
        // This function would typically trigger the save action in the child component
        // For now, we'll just toggle edit mode off.
        // The actual save logic is handled by `handleChildSave` which is passed to the child.
        if (activeTabRef.current && activeTabRef.current.triggerSave) {
            activeTabRef.current.triggerSave();
        } else {
            setIsEditMode(false);
        }
    };

    const handleDetailsCancel = () => {
        // Reset any draft changes if necessary, then exit edit mode
        setIsEditMode(false);
        // If creating, go back to accounts list
        if (isCreating) {
            setIsCreating(false);
            setDraftAccount(null);
            setViewMode('accounts');
        }
    };

    return (
        <PageContainer>
            <Panel>
                <PanelHeader>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {viewMode === 'details' && (
                                    <BtnIcon onClick={() => {
                                        if (isCreating) {
                                            setIsCreating(false);
                                            setDraftAccount(null);
                                        }
                                        setViewMode('accounts');
                                        setViewedAccount(null);
                                    }} title="Back to Accounts">
                                        ←
                                    </BtnIcon>
                                )}
                                <PanelTitle>
                                    {isCreating ? 'Create New Account' : (viewMode === 'accounts' ? 'Accounts' : `Profile`)}
                                </PanelTitle>
                            </div>

                            {/* Toggle Edit Mode in Accounts View */}
                            {viewMode === 'accounts' && (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {!isEditMode ? (
                                        <BtnIcon onClick={toggleEditMode} title="Edit Accounts">✎</BtnIcon>
                                    ) : (
                                        <>
                                            <Btn primary onClick={handleSaveAccounts} style={{ padding: '4px 12px', fontSize: '12px' }}>Save</Btn>
                                            <Btn onClick={handleCancelAccounts} style={{ padding: '4px 12px', fontSize: '12px' }}>Cancel</Btn>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Toggle Edit Mode in Details View */}
                            {viewMode === 'details' && (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {!isEditMode ? (
                                        <BtnIcon onClick={() => setIsEditMode(true)} title="Edit Details">✎</BtnIcon>
                                    ) : (
                                        <>
                                            <Btn primary onClick={handleDetailsSave} style={{ padding: '4px 12px', fontSize: '12px' }}>Save</Btn>
                                            <Btn onClick={handleDetailsCancel} style={{ padding: '4px 12px', fontSize: '12px' }}>Cancel</Btn>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Show what we are viewing */}


                        {viewMode === 'details' && (
                            <TabsNav style={{ marginTop: '8px' }}>
                                <Tab
                                    active={activeTab === 'settings'}
                                    onClick={() => setActiveTab('settings')}
                                    theme={theme}
                                >
                                    General
                                </Tab>
                                <Tab
                                    active={activeTab === 'profile'}
                                    onClick={() => setActiveTab('profile')}
                                    theme={theme}
                                >
                                    Objectives
                                </Tab>
                            </TabsNav>
                        )}
                    </div>
                </PanelHeader>

                <ContentWrapper>
                    {viewMode === 'accounts' ? (
                        <GridContainer>
                            {accounts.filter(acc => !pendingDeletions.has(acc.id)).map(acc => (
                                <AccountCard
                                    key={acc.id}
                                    account={acc}
                                    isActive={(userProfile.id || 1) === acc.id} // Check against active ID
                                    onActivate={handleSwitchAccount}
                                    onManage={handleManage}
                                    onDelete={handleDeleteAccount}
                                    theme={theme}
                                    isEditMode={isEditMode}
                                />
                            ))}
                            {isEditMode && (
                                <AccountCard
                                    isAddCard={true}
                                    onClick={handleAddAccount}
                                    theme={theme}
                                />
                            )}
                        </GridContainer>
                    ) : (
                        activeTab === 'settings' ? (
                            <Settings
                                isEmbedded={true}
                                isEditing={isEditMode}
                                ref={activeTabRef}
                                profileData={getCurrentProfileData()}
                                onSave={handleChildSave}
                            />
                        ) : (
                            <TradingProfile
                                isEmbedded={true}
                                isEditing={isEditMode}
                                ref={activeTabRef}
                                profileData={isCreating ? (draftAccount?.rules || {}) : (getCurrentProfileData().rules || {})}
                                onSave={handleChildSave}
                            />
                        )
                    )}
                </ContentWrapper>
            </Panel>
            {deleteModalOpen && (
                <ModalOverlay onClick={cancelDelete}>
                    <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
                        <ModalTitle theme={theme}>Confirm Deletion</ModalTitle>
                        <ModalText theme={theme}>
                            Are you sure you want to delete account "{accountToDelete?.name}"?
                            This action will be staged and finalized when you click Save.
                        </ModalText>
                        <ButtonRow>
                            <Btn theme={theme} onClick={cancelDelete}>Cancel</Btn>
                            <Btn
                                theme={theme}
                                onClick={confirmDelete}
                                style={{
                                    background: tokens(theme.palette.mode).redAccent[500],
                                    borderColor: tokens(theme.palette.mode).redAccent[500],
                                    color: 'white'
                                }}
                            >
                                Confirm
                            </Btn>
                        </ButtonRow>
                    </ModalContent>
                </ModalOverlay>
            )}
        </PageContainer>
    );
};

export default ProfileSettings;
